import posthog from 'posthog-js';
import { useCallback, useRef } from 'react';

import { AlloAuthUser } from '@Components/auth/utils';
import { IQueryData } from '@Reducers/common';
import { useAppSelector } from '@Redux/hooks';
import { UserRole } from '@Utils/constants';

enum Events {
  SCHEDULE_EVENT = 'schedule_event',
  CTA_EVENT = 'cta_event',
}

export enum ScheduleV2Step {
  VIEWED_SELECT_MEDIUM = 'viewed_select_medium',
  CONFIRM_LOCATION = 'confirm_location',
  VIEWED_SELECT_LANGUAGE = 'viewed_select_language',
  CONFIRM_LANGUAGE = 'confirm_language',
  VIEWED_SLOT_SELECTION = 'viewed_slot_selection',
  CONFIRM_SLOT = 'confirm_slot',
  VIEWED_CONFIRMATION = 'viewed_confirmation',
}

export enum ScheduleV3Step {
  VIEWED_SELECT_MEDIUM = 'viewed_select_medium',
  CONFIRM_MEDIUM = 'confirm_medium',
  VIEWED_SELECT_CITY = 'viewed_select_city',
  CONFIRM_CITY = 'confirm_city',
  VIEWED_SELECT_CLINIC = 'viewed_select_clinic',
  CONFIRM_CLINIC = 'confirm_clinic',
  VIEWED_SELECT_LANGUAGE = 'viewed_select_language',
  CONFIRM_LANGUAGE = 'confirm_language',
  VIEWED_SLOT_SELECTION = 'viewed_slot_selection',
  CONFIRM_SLOT = 'confirm_slot',
  VIEWED_CHECKOUT = 'viewed_checkout',
  CONFIRM_CHECKOUT = 'confirm_checkout',
  VIEWED_CONFIRMATION = 'viewed_confirmation',
}

export enum CtaEvents {
  PERSONAL_DETAILS = 'personal_details',
  ADDRESS = 'address',
  CHECKOUT = 'checkout',
  CITY = 'city',
  LANGUAGE = 'language',
  SLOT = 'slot',
  CLINIC = 'clinic',
  MEDIUM = 'medium',
  LAB_SLOT = 'lab_slot',
  INSTANT = 'instant',
  REVIEW = 'review',
  PAYMENT_INITIATED = 'payment_initiated',
  PAYMENT_SUCCESS = 'payment_success',
  PAYMENT_FAILED = 'payment_failed',
}

const usePosthog = () => {
  const auth: AlloAuthUser = useAppSelector((state) => state.auth.user);
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const queryDataRef = useRef<IQueryData>(null);
  queryDataRef.current = queryData;

  const schedulingEventCapture = useCallback(
    (properties: {
      step: string; // TODO: Change to enum
      version: number;
    }) => {
      if (!auth?.roles.includes(UserRole.PATIENT)) return;
      const payload = {
        ...properties,
        consultation_code: queryDataRef.current?.consultationCode,
        location: queryDataRef.current?.locationCode,
        selected_languages: queryDataRef.current?.selectedLanguages,
        consultation_id: queryDataRef.current?.consultationId,
        appointment_state: queryDataRef.current?.appointmentState,
      };
      posthog.capture(Events.SCHEDULE_EVENT, payload);
    },
    [],
  );

  const ctaEventCapture = useCallback((event: CtaEvents) => {
    if (!auth?.roles.includes(UserRole.PATIENT)) return;
    posthog.capture(Events.CTA_EVENT, {
      event,
      consultation_code: queryDataRef.current?.consultationCode,
      location: queryDataRef.current?.locationCode,
      selected_languages: queryDataRef.current?.selectedLanguages,
      consultation_id: queryDataRef.current?.consultationId,
      appointment_state: queryDataRef.current?.appointmentState,
    });
  }, []);

  return { schedulingEventCapture, ctaEventCapture };
};

export default usePosthog;
